/* eslint-disable no-redeclare */
import { isArray, isNotPresent, isObject, isPresent } from './typeUtils';

export const isNotEmpty = <T>(arr: T[]): boolean => isPresent(arr) && arr.length > 0;
export const isEmpty = <T>(arr: T[]): boolean => !isNotEmpty(arr);

export const isIn = <T>(value: T, list: T[]): boolean => isNotEmpty(list) && list.indexOf(value) !== -1;
export const isNotIn = <T>(value: T, list: T[]): boolean => isEmpty(list) || list.indexOf(value) === -1;

export const allPresent = (...arr: any[]): boolean =>
  arr.reduce((acc: boolean, item: any) => acc && isPresent(item), true);

export type Optional<T> = T | undefined;

export const isBlank = (str: string): boolean => isNotPresent(str) || str.trim().length === 0;
export const isNotBlank = (str: string): boolean => !isBlank(str);

export function values<T>(obj: { [key: string]: T }): T[];
export function values(obj: unknown): unknown[];
export function values(obj: unknown): unknown[] {
  return Object.keys(obj).map((key) => obj[key]);
}

export const mergeAttr = (...attributes: string[]): string => attributes.filter(isNotBlank).join(' ');

export const ifElse = <T>(value: T, defaultValue: T): T => (isPresent(value) ? value : defaultValue);

export const rangeFrom = (startWith: number, length: number): number[] =>
  Array.from({ length }).map((v, idx) => idx + startWith);

export const rangeBetween = (startInclusive: number, endInclusive: number): number[] =>
  Array.from({ length: endInclusive - startInclusive + 1 }).map((v, idx) => idx + startInclusive);

export const last = <T>(arr: T[]): T => (isEmpty(arr) ? undefined : arr[arr.length - 1]);

/**
 * Pad given value with placeholder as needed
 * @param value Value to pad
 * @param minLength Defines minimum length for value, default is 2
 * @param placeholder Character to use for padding. Defauls to '0'
 */
export const pad = (value: number | string, minLength = 2, placeholder = '0'): string => {
  const count = minLength - value.toString().length;
  return count > 0 ? new Array(count + 1).join(placeholder) + value : String(value);
};

export const sanitize = <T>(obj: T, check: (value: any) => boolean = isNotPresent): Partial<T> => {
  Object.keys(obj).forEach((key: string) => {
    if (isObject(obj[key])) {
      sanitize(obj[key]);
      if (isEmpty(Object.keys(obj[key]))) {
        delete obj[key];
      }
    } else if (check(obj[key])) {
      delete obj[key];
    }
  });
  return obj;
};

export const shift = <T>(array: T[], amount: number): T[] => {
  // eslint-disable-next-line prefer-spread
  array.unshift.apply(array, array.splice(amount));
  return array;
};

export const asArray = <T>(obj: T | T[]): T[] => (isArray(obj) ? obj : [obj]);

export const flatten = <T>(acc: T[], cur: T[]): T[] => (acc || []).concat(cur);

export const compareAsJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * This method can be used to strip some properties from an object. A common use case would be when
 * you want to spread properties onto a component `<input {...props} />` but need to remove some
 * properties that are not valid HTML props.
 */
export const cleanProps = <T = Record<string, unknown>>(props: T, propsToRemove: Array<keyof T>): Partial<T> => {
  const newProps = { ...props };

  propsToRemove.forEach((toRemove) => {
    delete newProps[toRemove];
  });

  return newProps;
};
